// Логотип приложения: словесный знак «Plan Flights» (Fredoka SemiBold/Bold),
// под ним ряд из пяти иконок — самолёт, багаж, машина, кровать, деньги.
// Название знака не переводится: локализованное название уходит в подсказку.
// Иконки нарисованы здесь, а не взяты из KindIcon: у знака свой набор (в том
// числе деньги, которых среди типов сегмента нет) и своя, более округлая
// пластика под Fredoka. Менять типы сегментов, не трогая логотип, — норма.
import type { MouseEvent, ReactElement } from 'react';
import { navigate } from '../../router';
import styles from './Logo.module.css';

export interface LogoProps {
  /** Локализованное название приложения — подсказка и подпись для скринридера. */
  title?: string;
  className?: string;
  /**
   * Куда ведёт знак. Задан — рисуется ссылкой: обычный клик переключает экран
   * без перезагрузки, а средняя кнопка и «открыть в новой вкладке» работают
   * как у всякой ссылки, потому что адрес настоящий.
   */
  href?: string;
  /**
   * 'stack' (по умолчанию) — слово над иконками, компактный знак десктопной
   * полосы и попапов. 'bar' — слово и иконки в одну строку на всю ширину
   * родителя: раскладка мобильной лого-полосы (см. MobileShellHeader), которая
   * сама и есть самая верхняя строка экрана, а не элемент внутри более широкой
   * панели. 'inline' — та же горизонтальная раскладка, что у 'bar' (слово
   * слева, иконки справа), но без width: 100% — знак встаёт по размеру своего
   * содержимого, а не растягивается. Нужен там, где знак — не вся строка сама
   * по себе, а часть более широкой строки наравне с соседними элементами
   * (см. однострочную шапку гостя в MobileShellHeader). Слово и набор иконок
   * везде те же самые — разный только их взаимный порядок и то, тянется ли
   * знак на всю ширину контейнера.
   */
  layout?: 'stack' | 'bar' | 'inline';
  /**
   * Ряд иконок можно спрятать целиком, не трогая слово, — например, когда он
   * не помещается рядом с соседними элементами строки (см. MobileShellHeader).
   * Половина иконки, обрезанная переполнением, — дефект, а не компромисс,
   * поэтому это именно «показать/убрать целиком», а не сжатие. true по
   * умолчанию — на всех прежних местах использования знака иконки не прячут.
   */
  showIcons?: boolean;
}

// Штрих 2.0 и крутые скругления прямоугольников — общий рисунок всех иконок
// приложения (см. KindIcon): под капсулы Liquid Glass углы стали круглее.
const ICON_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className: styles.icon,
  'aria-hidden': true,
  focusable: false,
};

function PlaneIcon(): ReactElement {
  return (
    <svg {...ICON_PROPS}>
      <path d="M21.4 2.6 3.2 9.2a.7.7 0 0 0-.06 1.3l5.1 2.4a1.2 1.2 0 0 1 .58.58l2.4 5.1a.7.7 0 0 0 1.3-.06z" />
      <path d="M21.4 2.6 11.1 12.9" />
    </svg>
  );
}

function BaggageIcon(): ReactElement {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3.2" y="7.4" width="17.6" height="12.6" rx="4" />
      <path d="M9 7.4V5.6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.8" />
      <path d="M8.8 11.4v4.6M15.2 11.4v4.6" />
    </svg>
  );
}

function CarIcon(): ReactElement {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3.6 14.4 5.2 9.6a2.5 2.5 0 0 1 2.4-1.7h8.8a2.5 2.5 0 0 1 2.4 1.7l1.6 4.8" />
      <rect x="2.6" y="14.4" width="18.8" height="4.2" rx="2.1" />
      <circle cx="7.4" cy="19.6" r="1.6" />
      <circle cx="16.6" cy="19.6" r="1.6" />
    </svg>
  );
}

function BedIcon(): ReactElement {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 20V7.6" />
      <path d="M21 20v-5.4a2.4 2.4 0 0 0-2.4-2.4H3" />
      <path d="M3 16.4h18" />
      <rect x="5.8" y="8.6" width="5" height="3.6" rx="1.8" />
    </svg>
  );
}

function MoneyIcon(): ReactElement {
  return (
    <svg {...ICON_PROPS}>
      <rect x="2.4" y="5.8" width="19.2" height="12.4" rx="4" />
      <circle cx="12" cy="12" r="2.9" />
      <path d="M6 10.2v3.6M18 10.2v3.6" />
    </svg>
  );
}

export function Logo({
  title,
  className,
  href,
  layout = 'stack',
  showIcons = true,
}: LogoProps = {}): ReactElement {
  const body = (
    <>
      <span className={styles.word}>
        <span className={styles.wordPlain}>Plan </span>
        <span className={styles.wordAccent}>Flights</span>
      </span>
      {showIcons && (
        <span className={styles.icons}>
          <PlaneIcon />
          <BaggageIcon />
          <CarIcon />
          <BedIcon />
          <MoneyIcon />
        </span>
      )}
    </>
  );

  const classes = [
    styles.logo,
    layout === 'bar' ? styles.bar : '',
    layout === 'inline' ? styles.inline : '',
    href ? styles.logoLink : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (!href) {
    return (
      <span className={classes} title={title}>
        {body}
      </span>
    );
  }

  // Модификаторы и не-левая кнопка остаются за браузером: это его способ
  // открыть ссылку в новой вкладке или окне, и перехватывать его нельзя.
  function handleClick(e: MouseEvent<HTMLAnchorElement>): void {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(href as string);
  }

  return (
    <a className={classes} href={href} title={title} onClick={handleClick}>
      {body}
    </a>
  );
}
